import { useRef } from 'react';
import logger from '../logger';
import { loadScript } from '../utils';

// List of whitelisted return URIs that Ops set up for our app.
// It includes all pages where we might display the Apple sign in button.
// We always use one of these as a redirect URI.
// See T55232
const whitelistedURIs = [
  'https://www.deviantart.com/join',
  'https://www.deviantart.com/users/login',
  'https://www.deviantart.com/users/join-login',
  'https://www.deviantart.com/_sisu/do/signup2',
  'https://www.deviantart.com/_sisu/do/signin',
];

function getRedirectURI() {
  const path = typeof window !== 'undefined' ? window.location.pathname : '';

  for (const uri of whitelistedURIs) {
    const u = new URL(uri);
    if (path.startsWith(u.pathname)) {
      return uri;
    }
  }

  return whitelistedURIs[0];
}

type SDKState = 'not-ready' | 'loading' | 'ready';
const SCRIPT_ID = 'apple-js-framework';
const SCRIPT_SRC =
  'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';

export default function useAppleSignIn(
  clientId: string,
  nonce: string | undefined,
  onSuccess: (response: AppleSignInAPI.SignInResponseI) => void
) {
  const sdkState = useRef<SDKState>('not-ready');
  const stateValue = useRef(`st-${Math.random()}`);

  const doSignIn = () => {
    AppleID.auth.signIn().then(
      response => {
        logger.info('AppleID auth success', response);
        if (
          response.authorization.state === stateValue.current &&
          response.authorization.code
        ) {
          onSuccess(response);
        }
      },
      (response: AppleSignInAPI.SignInErrorI) => {
        logger.info('AppleID auth failure', response);
      }
    );
  };

  const onScriptLoad = () => {
    AppleID.auth.init({
      clientId,
      scope: 'email',
      usePopup: true,
      state: stateValue.current,
      nonce,
      // Redirect isn't actually used because we use popup mode,
      // but authentication doesn't work without specifying it.
      // It must match whatever ops specified when setting up Apple Sign-in for our app, see T55232.
      // Some say it must also match the URI of the page where the button is displayed.
      // It's not mentioned anywhere in official docs, but we do it anyways.
      redirectURI: getRedirectURI(),
    });
    sdkState.current = 'ready';
    doSignIn();
  };

  const signIn = () => {
    switch (sdkState.current) {
      case 'not-ready':
        sdkState.current = 'loading';
        loadScript(SCRIPT_ID, SCRIPT_SRC, onScriptLoad);
        break;
      case 'ready':
        doSignIn();
        break;
      case 'loading':
      default:
        break;
    }
  };

  return { signIn };
}
